<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <AreaSolutionsBanner
        v-if="solutionData?.banner"
        :solution-alias="solutionAlias"
        :item-data="solutionData.banner"
      />

      <AreaSolutionsAnnouncement
        v-if="solutionData?.announcement"
        :item-data="solutionData.announcement"
      />

      <TheNavigation
        :trigger-id="triggerId"
        :navigation="navigation"
        :links="links"
      />

      <TheScrollTrigger
        v-for="(item, index) in contextComponents"
        :key="hash(item)"
        :offset="offset"
        @trigger="handleTrigger(item.meta?.navigable, index)"
      >
        <component
          :is="provideComponentName(item.component)"
          :id="provideSolutionsID(item.meta?.navigable, index)"
          :suite-data="item"
          url-connector="solution"
        />
      </TheScrollTrigger>

      <!-- <AreaSolutionsExample /> -->
    </template>
  </article>
</template>

<script setup lang="ts">
  import hash from 'object-hash';
  import type { ConcreteComponent } from 'vue';

  import type { ILink } from '@/components/Shared/TheNavigation.vue';

  const Architecture = resolveComponent('SuiteArchitecture');
  const Dfsi = resolveComponent('SuiteDfsi');
  const Faq = resolveComponent('SuiteFaq');
  const Overview = resolveComponent('SuiteOverview');
  const SuccessfulCase = resolveComponent('SuiteSuccessfulCase');
  const SuiteProduct = resolveComponent('SuiteSuiteProduct');
  const ListProducts = resolveComponent('SuiteListProducts');
  const ListMetricsStacked = resolveComponent('SuiteListMetricsStacked');
  const ListMetricsSplit = resolveComponent('SuiteListMetricsSplit');
  const ListFeaturesStacked = resolveComponent('SuiteListFeaturesStacked');
  const ListFeaturesSplit = resolveComponent('SuiteListFeaturesSplit');
  const HardwaresTabs = resolveComponent('SuiteHardwaresTabs');
  const HardwaresSplit = resolveComponent('SuiteHardwaresSplit');
  const ResourcesTabs = resolveComponent('SuiteResourcesTabs');
  const ResourcesSplit = resolveComponent('SuiteResourcesSplit');
  const TakeNextStep = resolveComponent('SuiteTakeNextStep');

  const route = useRoute();
  const { locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { isCN } = useLanguage();
  const { arrived } = useScrollArrivedBottom();

  const solutionsStore = useSolutionsStore();

  const { solutionData, contextComponents, navigation } =
    storeToRefs(solutionsStore);

  const { solution, generic } = route.params;

  const triggerId = ref<string>('');

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const solutionAlias = computed((): string => {
    const solutionParam = Array.isArray(solution) ? solution[0] : solution;
    const genericParam = Array.isArray(generic) ? generic[0] : generic;
    return genericParam ? `${solutionParam}/${genericParam}` : solutionParam;
  });

  const links = computed((): ILink[] => [
    {
      name: tGlobal('solutions.request-a-demo'),
      link: `/${locale.value}/marketplace/solutions/${solutionAlias.value}/request-demo`,
      trackingCode: isCN.value ? 'tracking_requestdemo' : undefined,
      hasArrow: true,
      handler: handleClickRequestDemo,
    },
  ]);

  const { status: GET_SOLUTIONS_STATUS } =
    await solutionsStore.USE_GET_SOLUTIONS();

  const isLoading = computed(
    (): boolean => GET_SOLUTIONS_STATUS.value === 'pending'
  );

  const provideComponentName = (
    code: string | null
  ): string | ConcreteComponent => {
    switch (code) {
      case 'architecture':
        return Architecture;
      case 'faq':
        return Faq;
      case 'overview':
        return Overview;
      case 'successful-case':
        return SuccessfulCase;
      case 'suite-product':
        return SuiteProduct;
      case 'dfsi':
        return Dfsi;
      case 'list-products':
        return ListProducts;
      case 'list-metrics-stacked':
        return ListMetricsStacked;
      case 'list-metrics-split':
        return ListMetricsSplit;
      case 'list-features-stacked':
        return ListFeaturesStacked;
      case 'list-features-split':
        return ListFeaturesSplit;
      case 'hardwares-tabs':
        return HardwaresTabs;
      case 'hardwares-split':
        return HardwaresSplit;
      case 'resources-tabs':
        return ResourcesTabs;
      case 'resources-split':
        return ResourcesSplit;
      case 'take-next-step':
        return TakeNextStep;
      default:
        return '';
    }
  };

  const handleTrigger = (
    navigable: string | undefined,
    index: number
  ): void => {
    if (!arrived.value) {
      const id = provideSolutionsID(navigable, index);
      if (id && triggerId.value !== id) {
        triggerId.value = id;
      }
    }
  };

  const handleClickRequestDemo = (): void => {
    if (solutionAlias.value === 'iems-solutions') {
      gtagSolutionIEMS('request-demo', locale.value);
    }
    if (solutionData.value?.banner?.title) {
      window.gtag?.('event', 'site_click', {
        Item: 'RAD_Solution',
        Item_Name: solutionData.value.banner.title,
      });
    }
  };

  watch(arrived, (value) => {
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  watch(
    [GET_SOLUTIONS_STATUS],
    (value) => {
      if (value.includes('error')) {
        showError({ statusCode: 404 });
      }
    },
    { immediate: true }
  );

  useSeoMeta({
    title: () => solutionData.value?.banner.title || '',
    ogTitle: () => provideWebTitle(solutionData.value?.banner.title),
    twitterTitle: () => provideWebTitle(solutionData.value?.banner.title),
    description: () => solutionData.value?.banner.description || '',
    ogDescription: () => solutionData.value?.banner.description || '',
    twitterDescription: () => solutionData.value?.banner.description || '',
  });
</script>
